import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { IconPlus, IconX } from '@tabler/icons-react';

import PageContainer from '@shared/components/container/PageContainer';
import Spinner from '@shared/components/spinner/Spinner';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions } from '@shared/store/ducks';

import { useStyles } from './styles';
import { AgentProfileList } from './components/AgentProfileList';
import { AddAgentProfileModal } from './components/AddAgentProfileModal';
import { EditAgentProfileModal } from './components/EditAgentProfileModal';
import { ViewAgentProfileModal } from './components/ViewAgentProfileModal';
import { DeleteAgentProfileModal } from './components/DeleteAgentProfileModal';
import { useAgentProfiles } from './hooks/useAgentProfiles';
import { useFilteredAgentProfiles } from './hooks/useFilteredAgentProfiles';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { AgentProfileResponseDto } from '@shared/services/apiService';

const AssistantsPage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentCompany = useAppSelector((state) => state.auth.authenticatedUserCompany);
  const currentUserRole = useAppSelector((state) => state.auth.authenticatedUser?.role);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AgentProfileResponseDto | null>(null);
  const [profileOverrides, setProfileOverrides] = useState<Record<string, AgentProfileResponseDto>>(
    {},
  );
  const [searchQuery, setSearchQuery] = useState('');

  const { customAgentProfiles, isLoading, refetch } = useAgentProfiles();
  const visibleCustomAgentProfiles = useMemo(
    () => customAgentProfiles?.map((profile) => profileOverrides[profile.id] ?? profile),
    [customAgentProfiles, profileOverrides],
  );
  const { filteredProfiles, totalCount, filteredCount, hasActiveSearch } = useFilteredAgentProfiles(
    visibleCustomAgentProfiles,
    searchQuery,
  );

  useEffect(() => {
    dispatch(actions.customizer.toggleLayout('full'));
  }, [dispatch]);

  const handleAddProfile = () => {
    setSelectedProfile(null);
    setShowAddModal(true);
  };
  const handleCopyProfile = async (profile: { name: string; prompt?: string }) => {
    if (!profile.prompt) {
      toast.error(t('assistantsPage.copyPrompt.empty'));
      return;
    }

    try {
      await navigator.clipboard.writeText(profile.prompt);
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 3000,
        icon: false,
        data: {
          title: t('assistantsPage.copyPrompt.title'),
          description: t('assistantsPage.copyPrompt.description', { name: profile.name }),
        },
      });
    } catch {
      toast.error(t('assistantsPage.copyPrompt.failed'));
    }
  };

  const handleEditProfile = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setShowEditModal(true);
  };

  const handleViewProfile = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setShowViewModal(true);
  };

  const handleDeleteProfile = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setSelectedProfile(null);
  };

  const handleProfileUpdated = (profile: AgentProfileResponseDto) => {
    setSelectedProfile(profile);
    setProfileOverrides((previous) => ({ ...previous, [profile.id]: profile }));
  };

  const handleProfileAction = (profile?: AgentProfileResponseDto) => {
    if (profile) handleProfileUpdated(profile);
    refetch();
    handleCloseModals();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <PageContainer
      title={t('assistantsPage.header.title')}
      companyName={currentCompany?.title}
      description={t('assistantsPage.header.title')}>
      <Box className={classes.main}>
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {t('assistantsPage.header.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<IconPlus size={20} />}
            onClick={handleAddProfile}
            disabled={currentUserRole === UserRolesEnum.VIEWER}
            className={classes.addButton}>
            {t('assistantsPage.actions.addProfile')}
          </Button>
        </Box>

        {totalCount > 0 && (
          <Box className={classes.toolbar}>
            <TextField
              className={classes.searchField}
              size="small"
              placeholder={t('assistantsPage.toolbar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t('assistantsPage.modals.close')}
                      onClick={() => setSearchQuery('')}>
                      <IconX size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Typography variant="body2" color="text.secondary" className={classes.countLabel}>
              {hasActiveSearch
                ? t('assistantsPage.toolbar.showingCount', {
                    filtered: filteredCount,
                    total: totalCount,
                  })
                : t('assistantsPage.toolbar.profileCount', { count: totalCount })}
            </Typography>
          </Box>
        )}

        <AgentProfileList
          profiles={filteredProfiles}
          searchQuery={searchQuery}
          totalCount={totalCount}
          onEdit={handleEditProfile}
          onCopy={handleCopyProfile}
          onView={handleViewProfile}
          onDelete={handleDeleteProfile}
        />
      </Box>

      <AddAgentProfileModal
        open={showAddModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
      />

      <EditAgentProfileModal
        open={showEditModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
        onProfileUpdated={handleProfileUpdated}
        profile={selectedProfile}
      />

      <ViewAgentProfileModal
        open={showViewModal}
        onClose={handleCloseModals}
        profile={selectedProfile}
      />

      <DeleteAgentProfileModal
        open={showDeleteModal}
        onClose={handleCloseModals}
        onSuccess={handleProfileAction}
        profile={selectedProfile}
      />
    </PageContainer>
  );
};

export { AssistantsPage };
